<template>
<div class="d-flex align-center gap10">
  <v-btn rounded
    v-show="!googleUser"
    color="profileAnchorColor"
    class="text-none whiteColor--text googleLoginButton"
    :loading="connecting"
    @click="signIn">
    <span>Connect</span>
  </v-btn>
  <a v-show="googleUser && !compact" class="connected-status">
    <span class="material-icons">check_circle</span>
    <span>Connected</span>
  </a>
  <a v-show="googleUser"
    @click="signOut"
    class="signout-btn">
    <span class="material-icons-outlined">delete_forever</span>
  </a>
</div>
</template>

<script>
/**
 * Google connect button (GIS authorization-code flow).
 *
 * The popup's one-time code is exchanged server-side (connectGoogleAccount),
 * which stores a refresh token on the account level - so a connection made
 * here is immediately usable by the White Glove Settings/Wins tabs, and a
 * connection made there is restored here on mount without another popup.
 * The short-lived access token is injected into gapi.client, so all existing
 * browser-side Search Console queries keep working unchanged.
 */
import { EventEmitter } from '@/utils/Event.js';
import { requestGoogleAuthCode, restoreBackendGoogleSession, setGoogleBrowserSignedOut } from '@/utils/search-console-helper.js';

export default {
  props: {
    /** When true, hide the inline "Connected" label (parent shows its own badge). */
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      googleUser: null,
      connecting: false,
    }
  },
  methods: {
    async restoreSession () {
      const user = await restoreBackendGoogleSession(this.$store)
      if (user) {
        this.googleUser = user
        EventEmitter.dispatch('GOOGLE_USER_UPDATE', user)
      }
    },
    async signIn () {
      if (this.connecting) return
      this.connecting = true
      try {
        // Connect = opt back in; a previously signed-out browser can reuse
        // the backend account without a popup.
        setGoogleBrowserSignedOut(false)
        const existing = await restoreBackendGoogleSession(this.$store, { force: true })
        if (existing) {
          this.googleUser = existing
          EventEmitter.dispatch('GOOGLE_USER_UPDATE', existing)
          return
        }
        const authCode = await requestGoogleAuthCode()
        const response = await this.$store.dispatch('connectGoogleAccount', { authCode })
        if (!(response.data && response.data.status === 'SUCCESS')) {
          throw new Error((response.data && response.data.msg) || 'Could not connect the Google account')
        }
        const user = await restoreBackendGoogleSession(this.$store)
        if (user) {
          this.googleUser = user
          EventEmitter.dispatch('GOOGLE_USER_UPDATE', user)
        }
      } catch (error) {
        if (!error.popupClosed) {
          this.$notify({ group: 'info', type: 'error', text: error.message || 'Google sign-in failed' })
        }
      } finally {
        this.connecting = false
      }
    },
    signOut () {
      // Removes the server-side account too (revoked; site connections flip
      // to disconnected) so the White Glove Settings tab reflects it. Data
      // and settings are kept; reconnecting restores everything.
      setGoogleBrowserSignedOut(true)
      this.googleUser = null
      this.$store.commit('updateGoogleUser', null)
      EventEmitter.dispatch('GOOGLE_USER_UPDATE', null)
      this.$store.dispatch('disconnectGoogleAccount').catch(() => {})
    },
  },
  mounted() {
    this.restoreSession()
  },
}
</script>

<style lang="scss" scoped>
.connected-status {
  display: flex;
  align-items: center;
  gap: 2px;
  .material-icons {
    font-size: 1.2rem;
    color: var(--v-green7Color-base);
  }
  span {
    font-size: 0.9rem;
    font-weight: 700;
    color: var(--v-green7Color-base);
  }
}
.signout-btn {
  cursor: pointer;
  span {
    font-size: 1.6rem;
    color: var(--v-profileAnchorColor-base);
  }
}
</style>
